import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { BrandHeader } from "@/components/BrandHeader";
import { Footer } from "@/components/Footer";
import { ChevronLeft } from "lucide-react";

const COMPANY = "Sporty People";
const TITULAR = "Carmen Arufe";
const NIF = "44045038S";
const DIRECCION = "Calle Estrella Naos, 4, 28045 Madrid";
const EMAIL = "info@sportypeople.es";
const PHONE = "+34 667 396 767";

// Textos legales estándar. Datos fiscales concretos (razón social, CIF, domicilio) a completar por la empresa.
const LEGAL = {
  "aviso-legal": {
    title: "Aviso legal",
    sections: [
      { h: "Titular del sitio", p: `Este sitio web de gestión de actividades para Grupos de Empresa es titularidad de ${TITULAR} (${COMPANY}), con NIF ${NIF} y domicilio en ${DIRECCION} (en adelante, "el Titular"). Correo de contacto: ${EMAIL}.` },
      { h: "Objeto", p: "El sitio tiene como finalidad la información, gestión e inscripción en las actividades de ocio y tiempo libre ofrecidas a los grupos de empresa. El acceso y uso del sitio atribuye la condición de usuario e implica la aceptación de las presentes condiciones." },
      { h: "Propiedad intelectual e industrial", p: "Todos los contenidos del sitio (textos, imágenes, logotipos, diseño y código) son titularidad del Titular o de terceros que han autorizado su uso. Queda prohibida su reproducción, distribución o transformación sin autorización expresa." },
      { h: "Responsabilidad", p: "El Titular no se responsabiliza de un uso indebido del sitio ni de los daños derivados de la falta de disponibilidad temporal por causas técnicas. Se reserva el derecho a modificar los contenidos y actividades sin previo aviso." },
      { h: "Legislación aplicable", p: "Las presentes condiciones se rigen por la legislación española. Para cualquier controversia, las partes se someten a los juzgados y tribunales que correspondan conforme a la normativa aplicable." },
    ],
  },
  privacidad: {
    title: "Política de privacidad",
    sections: [
      { h: "Responsable del tratamiento", p: `${TITULAR} (${COMPANY}), NIF ${NIF}, con domicilio en ${DIRECCION}. Correo de contacto en materia de protección de datos: ${EMAIL}.` },
      { h: "Datos que tratamos", p: "Datos identificativos y de contacto (nombre, email, teléfono), datos de los participantes (nombre y edad) necesarios para la organización de la actividad, y datos de pago gestionados de forma segura por nuestro proveedor de pagos. No almacenamos los datos completos de tu tarjeta." },
      { h: "Finalidad", p: "Gestionar tu inscripción y participación en las actividades, tramitar cobros y devoluciones, enviarte confirmaciones y comunicaciones relacionadas con la actividad y, si lo autorizas, informarte de futuras actividades." },
      { h: "Legitimación", p: "La base legal es la ejecución de la relación contractual derivada de tu inscripción, el consentimiento que nos otorgas y el cumplimiento de obligaciones legales." },
      { h: "Conservación", p: "Conservamos tus datos durante el tiempo necesario para prestar el servicio y cumplir las obligaciones legales aplicables. Después se bloquean y se suprimen." },
      { h: "Destinatarios", p: "Únicamente compartimos datos con los proveedores necesarios para prestar el servicio (por ejemplo, pasarela de pago y servicio de correo), que actúan como encargados del tratamiento." },
      { h: "Tus derechos", p: `Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a ${EMAIL}. También puedes reclamar ante la Agencia Española de Protección de Datos (www.aepd.es).` },
    ],
  },
  cookies: {
    title: "Política de cookies",
    sections: [
      { h: "Qué son las cookies", p: "Las cookies son pequeños archivos que se almacenan en tu dispositivo al navegar. Nos permiten recordar tu sesión y mejorar tu experiencia." },
      { h: "Cookies que utilizamos", p: "Utilizamos cookies técnicas y de sesión imprescindibles para el funcionamiento del sitio (por ejemplo, para mantener tu acceso y tu inscripción en curso). Estas cookies no requieren consentimiento por ser estrictamente necesarias." },
      { h: "Gestión", p: "Puedes configurar o eliminar las cookies desde los ajustes de tu navegador. Si las desactivas, es posible que algunas funciones del sitio no estén disponibles." },
    ],
  },
  condiciones: {
    title: "Condiciones de contratación",
    sections: [
      { h: "Proceso de inscripción", p: "La inscripción se realiza a través del formulario del sitio. El precio total se calcula automáticamente en función de las opciones seleccionadas y de la edad de cada participante, e incluye impuestos cuando corresponda." },
      { h: "Formas de pago", p: "Puedes pagar con tarjeta de crédito/débito, Google Pay, Apple Pay y PayPal a través de la pasarela segura de Stripe, o mediante los métodos manuales indicados (Bizum, transferencia bancaria o PayPal manual). En los pagos manuales, la plaza se confirma al recibir el importe." },
      { h: "Confirmación de plaza", p: "La plaza queda confirmada una vez recibido el pago. En caso de actividades completas, podrás unirte a la lista de espera y recibirás una notificación con una reserva temporal si queda una plaza libre." },
      { h: "Precios y disponibilidad", p: "Los precios y la disponibilidad de plazas pueden variar. Nos reservamos el derecho a cancelar o modificar una actividad por causas justificadas, informando a las personas inscritas y procediendo, en su caso, a la devolución correspondiente." },
    ],
  },
  cancelaciones: {
    title: "Política de cancelación y reembolsos",
    sections: [
      { h: "Cancelación por parte del participante", p: "Las condiciones concretas de cancelación y los importes reembolsables se indican en la ficha de cada actividad, ya que dependen de la antelación con la que se comunique la baja y de los gastos ya comprometidos." },
      { h: "Cómo solicitar la cancelación", p: `Para cancelar tu inscripción, escríbenos a ${EMAIL} o por WhatsApp al ${PHONE} indicando tu número de referencia. Tramitaremos tu solicitud lo antes posible.` },
      { h: "Reembolsos", p: "Cuando proceda una devolución, se realizará por el mismo medio de pago utilizado. Los reembolsos de pagos con tarjeta pueden tardar varios días hábiles en reflejarse según tu banco." },
      { h: "Cancelación por parte de la organización", p: "Si una actividad se cancela por causas ajenas al participante, se reembolsará el importe abonado íntegramente o se ofrecerá una alternativa equivalente." },
    ],
  },
  contacto: {
    title: "Contacto",
    sections: [
      { h: "Estamos para ayudarte", p: "¿Tienes dudas sobre una actividad, tu inscripción o un pago? Contáctanos y te responderemos lo antes posible." },
      { h: "Correo electrónico", p: EMAIL },
      { h: "Teléfono y WhatsApp", p: PHONE },
      { h: "Organización", p: `Actividades para Grupos de Empresa, operadas por ${TITULAR} (${COMPANY}), NIF ${NIF}, ${DIRECCION}.` },
    ],
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const doc = LEGAL[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <BrandHeader />
      <div className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} data-testid="legal-back" className="flex items-center gap-1 text-sm font-bold text-slate-500 hover:brand-navy mb-6"><ChevronLeft size={18} /> Volver</button>
        {doc ? (
          <article className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-100 shadow-sm" data-testid={`legal-${slug}`}>
            <h1 className="text-3xl sm:text-4xl font-black brand-navy mb-6">{doc.title}</h1>
            <div className="space-y-6">
              {doc.sections.map((s, i) => (
                <section key={i}>
                  <h2 className="text-lg font-black brand-navy mb-1">{s.h}</h2>
                  <p className="text-slate-600 leading-relaxed">{s.p}</p>
                </section>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-8">Última actualización: {new Date().toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}.</p>
          </article>
        ) : (
          <div className="text-center py-20">
            <p className="text-slate-500 font-bold mb-4">Documento no encontrado.</p>
            <Link to="/inicio" className="text-[#003284] font-bold underline">Volver al inicio</Link>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
